import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  PersistQueryClientOptions,
  PersistQueryClientProvider,
} from '@tanstack/react-query-persist-client';
import { Role, checkIfInvalidGrantError, rolesManipulator } from 'auth';
import { useCallback, useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { useAuth } from 'react-oidc-context';
import { matchPath } from 'react-router-dom';
import { AppRoutes } from '@/AppRoutes.tsx';
import { queryClient } from '@/modules/common/query.ts';
import { commonPaths } from '@/modules/common/routes.tsx';
import { getPersistentParamsSearch } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import {
  CURRENT_USER_QUERY_KEY,
  USER_SIGN_UP_NOW_QUERY_KEY,
} from '@/modules/auth/queries/user.tsx';
import { authPaths } from '@/modules/auth/routes.tsx';
import {
  CURRENT_ENTITY_CREATING_KEY,
  CURRENT_ENTITY_QUERY_KEY,
  CURRENT_REGISTER_APPLY_ID_QUERY_KEY,
  CURRENT_REGISTER_CLASS_APPLY_ID_QUERY_KEY,
  FOLLOW_UP_ENTITY_ID_QUERY_KEY,
} from '@/modules/entity/queries/entity.tsx';
import { SHOW_MODAL_COMPLETE_KYC } from '@/modules/investor-dashboard/queries/entity.ts';
import { Spinner } from 'ui';

const persister = createSyncStoragePersister({
  storage: window.localStorage,
});

const persistableQueries = {
  [CURRENT_USER_QUERY_KEY]: true,
  [CURRENT_ENTITY_CREATING_KEY]: true,
  [USER_SIGN_UP_NOW_QUERY_KEY]: true,
  [CURRENT_ENTITY_QUERY_KEY]: true,
  [CURRENT_REGISTER_APPLY_ID_QUERY_KEY]: true,
  [CURRENT_REGISTER_CLASS_APPLY_ID_QUERY_KEY]: true,
  [FOLLOW_UP_ENTITY_ID_QUERY_KEY]: true,
  [SHOW_MODAL_COMPLETE_KYC]: true,
};

const persistOptions: Omit<PersistQueryClientOptions, 'queryClient'> = {
  persister,
  dehydrateOptions: {
    dehydrateMutations: false,
    shouldDehydrateQuery: query =>
      (query.queryKey[0] as string) in persistableQueries && query.state.status === 'success',
  },
};

const publicPages = [
  authPaths.static.signup,
  authPaths.static.invitation,
  commonPaths.static.checkShareCertificate,
  commonPaths.static.notFound,
  commonPaths.static.acknowledgementsAndDeclarations,
  commonPaths.static.privacyPolicy,
];

function App() {
  const [isHydrated, setIsHydrated] = useState(false);
  const auth = useAuth();
  const [isCheckedAuth, setIsCheckedAuth] = useState(false);
  const isInvalidGrantError = checkIfInvalidGrantError(auth);
  const userRole = rolesManipulator.current_user_role;

  useEffect(() => {
    if (!isCheckedAuth && !auth.isLoading) {
      auth
        .signinSilent()
        .catch(error => {
          console.warn('[sr] Silent sign-in failed:', error);
        })
        .finally(() => setIsCheckedAuth(true));
    }
  }, [auth, isCheckedAuth]);

  const handleHydrationSuccess = useCallback(() => {
    setIsHydrated(true);
    console.log('Query cache dehydrated ✅');
  }, []);

  useEffect(() => {
    const isPublicAccessPage = publicPages.some(path => matchPath(path, location.pathname));
    if (isInvalidGrantError && !auth.isLoading && !isPublicAccessPage) {
      auth.signinRedirect({
        redirect_uri: `${location.origin}${location.pathname}?${getPersistentParamsSearch(
          userRole || Role.SR_ISSUER,
        ).toString()}`,
      });
    }
  }, [auth, isInvalidGrantError, userRole]);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions}
      onSuccess={handleHydrationSuccess}
    >
      {!isHydrated || !isCheckedAuth ? (
        <div className="flex h-[100dvh] w-full items-center justify-center">
          <Spinner size="lg" />
        </div>
      ) : (
        <AppRoutes />
      )}
      <Toaster position="top-center" />
      <ReactQueryDevtools initialIsOpen={false} />
    </PersistQueryClientProvider>
  );
}

export default App;
